import { useEffect, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import ReCAPTCHA from 'react-google-recaptcha'
import { Link, useNavigate } from 'react-router-dom'
import {
  getRedirectUrl,
  hideKAlert,
  markAuthenticated,
  removeAccess,
  setAccessToken,
  setUserAccess,
  showKAlert,
  submitPinCode,
} from '../helpers'
import { t } from '../locale'
import { isMicrosoftLoginEnabled } from '../service/microsoft'
import v from '../validators'

interface MicrosoftLoginResult {
  error?: string
  stage?: string
  pendingToken?: string
  accessToken?: string
  pinCode?: string
  pinRequired?: boolean
}

function Login(props: any) {
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isEmailValid, setIsEmailValid] = useState(true)
  const [isPasswordValid, setIsPasswordValid] = useState(true)
  const [recaptchaEnabled, setRecaptchaEnabled] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const [isRecaptchaValid, setIsRecaptchaValid] = useState(true)
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false)
  const [isMicrosoftEnabled, setIsMicrosoftEnabled] = useState(false)

  useEffect(() => {
    if (process.env.REACT_APP_RECAPTCHA_ENABLED === '1') setRecaptchaEnabled(true)
  }, [])

  // Microsoft sign-in only exists for tenants that configured their own Entra app.
  useEffect(() => {
    isMicrosoftLoginEnabled().then(setIsMicrosoftEnabled)
  }, [])

  useEffect(() => {
    if (props.logout) {
      fetch(`${process.env.REACT_APP_API_URL}/access`, {
        method: 'DELETE',
      }).then(() => {
        removeAccess()
        hideKAlert()
        navigate('/user/login')
      })
    }
  }, [props.logout, navigate])

  function getAccess() {
    fetch(`${process.env.REACT_APP_API_URL}/access/get`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email,
        password: password,
        recaptchaToken: recaptchaToken,
      }),
    })
      .then(r => r.json())
      .then(r => {
        if (r.stage === '2fa_required' && r.pendingToken) {
          setAccessToken(r.accessToken)
          navigate('/user/twofa-verify/' + encodeURIComponent(r.pendingToken))
          return
        }
        if (!!r.accessToken) {
          setAccessToken(r.accessToken)
          navigate(
            '/user/pincode-validation/' + (process.env.REACT_APP_ENV === 'dev' ? r.pinCode : '')
          )
        }
      })
  }

  function handleMicrosoftResult(result: MicrosoftLoginResult) {
    setIsMicrosoftLoading(false)

    if (result.error) {
      showKAlert('danger', result.error)
      return
    }

    if (result.stage === '2fa_required' && result.pendingToken && result.accessToken) {
      setAccessToken(result.accessToken)
      navigate('/user/twofa-verify/' + encodeURIComponent(result.pendingToken))
      return
    }

    if (result.accessToken) {
      setAccessToken(result.accessToken)

      if (result.pinRequired === false) {
        submitPinCode('', result.accessToken).then(r => {
          if (r.userAccess) {
            sessionStorage.removeItem('accessToken')
            markAuthenticated()
            setUserAccess(r.userAccess)
            hideKAlert()
            navigate(getRedirectUrl())
          }
        })
        return
      }

      navigate(
        '/user/pincode-validation/' +
          (process.env.REACT_APP_ENV === 'dev' && result.pinCode ? result.pinCode : '')
      )
    }
  }

  async function getMicrosoftAccess() {
    const apiUrl = process.env.REACT_APP_API_URL

    if (!apiUrl) {
      showKAlert('danger', 'microsoftAuthenticationFailed')
      return
    }

    const subdomain = window.location.hostname.split('.')[0]

    if (!subdomain) {
      showKAlert('danger', 'microsoftTenantMissing')
      return
    }

    const popup = window.open('', 'microsoftLogin', 'width=600,height=700')

    if (!popup) {
      showKAlert('danger', 'microsoftPopupBlocked')
      return
    }

    setIsMicrosoftLoading(true)

    try {
      const url = new URL(`${apiUrl}/microsoft/login`)
      url.searchParams.set('origin', window.location.origin)
      url.searchParams.set('subdomain', subdomain)

      const response = await fetch(url.toString())
      const startResult = await response.json()

      if (!response.ok || !startResult.authorizationUrl || !startResult.attemptId) {
        popup.close()
        setIsMicrosoftLoading(false)
        showKAlert('danger', 'microsoftAuthenticationFailed')
        return
      }

      popup.location.href = startResult.authorizationUrl

      const deadline = Date.now() + 5 * 60 * 1000

      const poll = window.setInterval(async () => {
        if (Date.now() >= deadline) {
          window.clearInterval(poll)
          if (!popup.closed) popup.close()
          setIsMicrosoftLoading(false)
          showKAlert('danger', 'microsoftAuthenticationExpired')
          return
        }

        try {
          const statusResponse = await fetch(
            `${apiUrl}/microsoft/login/status/${encodeURIComponent(startResult.attemptId)}`
          )

          const result = await statusResponse.json()

          if (statusResponse.status === 404) {
            window.clearInterval(poll)
            setIsMicrosoftLoading(false)
            showKAlert('danger', result.error ?? 'microsoftAuthenticationExpired')
            return
          }

          if (result.pending) return

          window.clearInterval(poll)

          if (!popup.closed) popup.close()

          handleMicrosoftResult(result)
        } catch {
          // Keep polling until the short authentication timeout expires.
        }
      }, 750)
    } catch {
      popup.close()
      setIsMicrosoftLoading(false)
      showKAlert('danger', 'microsoftAuthenticationFailed')
    }
  }

  function handleEmail(e: any) {
    const value = e.target.value.trimStart()
    setEmail(value)
    setIsEmailValid(v.email(value))
  }
  function handlePassword(e: any) {
    const value = e.target.value.trim()
    setPassword(value)
    setTimeout(() => {
      setIsPasswordValid(value !== '')
    }, 0)
  }

  function handleRecaptchaChange(token: string | null) {
    setRecaptchaToken(token)
    setIsRecaptchaValid(!!token)
  }

  function handleSubmit(e: any) {
    e.preventDefault()

    setIsEmailValid(v.email(email))
    setIsPasswordValid(password.trim() !== '')

    if (recaptchaEnabled && !recaptchaToken) {
      setIsRecaptchaValid(false)
      return
    }

    if (v.email(email) && password.trim() !== '') {
      getAccess()
    }
  }

  return (
    <>
      <div className='container  justify-content-center align-items-center'>
        <div className='col-12 col-md-5 col-lg-4 px-4 mx-auto bg-white overflow-hidden text-center'>
          <Link to='/' className='d-inline-block p-4'>
            <img src='/KYC_logo_black.png' style={{ maxHeight: '45px' }} alt='DS|Kyc'></img>
          </Link>
          <main className='form-signin'>
            <form onSubmit={handleSubmit} className='input'>
              <div className='form-floating mt-3'>
                <Form.Control
                  className='border-primary bg-white rounded-md w-100'
                  id='email'
                  type='email'
                  placeholder='name@example.com'
                  value={email}
                  onChange={handleEmail}
                  isInvalid={!isEmailValid}
                  required
                  autoComplete='on'
                  autoFocus
                />
                <Form.Label htmlFor='email' className='small'>
                  {t('Email')}
                </Form.Label>
              </div>
              <div className='form-floating mt-3'>
                <Form.Control
                  className='border-primary bg-white rounded-md w-100'
                  id='password'
                  type='password'
                  placeholder={t('password')}
                  value={password}
                  onChange={handlePassword}
                  isInvalid={!isPasswordValid}
                  required
                  autoComplete='on'
                />
                <Form.Label htmlFor='password' className='small'>
                  {t('password')}
                </Form.Label>
              </div>

              {recaptchaEnabled && (
                <div className='form-floating mt-3'>
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
                    onChange={handleRecaptchaChange}
                    onExpired={() => setRecaptchaToken(null)}
                  />
                </div>
              )}
              {recaptchaEnabled && !isRecaptchaValid && (
                <div className='text-danger small mt-1 text-start'>
                  {t('Please verify that you are not a robot')}
                </div>
              )}

              <div className='d-flex align-items-center justify-content-between my-3'>
                <Link to='http://ds-kyc.de/company/create'>{t('Register')}</Link>

                <Button
                  className='btn btn-primary rounded-pill px-3 btn btn-primary btn-sm ml-auto px-3 rounded-pill'
                  type='submit'
                  onClick={handleSubmit}
                >
                  {t('logIn')} »
                </Button>
              </div>
            </form>

            {isMicrosoftEnabled && (
              <div className='my-3'>
                <Button
                  variant='light'
                  className='w-100 d-flex align-items-center justify-content-center gap-2 border'
                  type='button'
                  onClick={getMicrosoftAccess}
                  disabled={isMicrosoftLoading}
                  style={{
                    minHeight: '42px',
                    borderColor: '#8c8c8c',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    color: '#2f2f2f',
                    fontWeight: 500,
                  }}
                >
                  <img src='/microsoft-logo.svg' alt='' width='20' height='20' />
                  <span>
                    {isMicrosoftLoading ? t('microsoftSigningIn') : t('logInWithMicrosoft')}
                  </span>
                </Button>
              </div>
            )}

            <Link to='/user/forgot-password'>{t('forgotYourPassword')} ?</Link>
          </main>
        </div>
      </div>
    </>
  )
}

export default Login
